import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { ThemePalette } from '@angular/material/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
  selector: 'ts-checkbox',
  standalone: true,
  imports: [MatCheckboxModule],
  templateUrl: './ts-checkbox.component.html',
  styleUrls: ['./ts-checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TsCheckboxComponent),
      multi: true,
    },
  ],
  host: {
    class: 'ts-checkbox',
  },
})
export class TsCheckboxComponent implements ControlValueAccessor {
  @Input() id: string;
  @Input() name: string | null = null;
  @Input() color: ThemePalette = 'primary';
  @Input() labelPosition: 'before' | 'after' = 'after';
  @Input() disableRipple = false;
  @Input() required = false;
  @Input() value: string;
  @Input() tabIndex: number;
  @Input() ariaLabel: string;
  @Input() ariaLabelledby: string | null = null;
  @Input() ariaDescribedby: string;

  @Input()
  set checked(value: boolean) {
    this._checked = coerceBooleanProperty(value);
  }

  get checked(): boolean {
    return this._checked;
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }

  get disabled(): boolean {
    return this._disabled || this._formDisabled;
  }

  @Input()
  set indeterminate(value: boolean) {
    this._indeterminate = coerceBooleanProperty(value);
  }

  get indeterminate(): boolean {
    return this._indeterminate;
  }

  @Output() readonly change = new EventEmitter<MatCheckboxChange>();
  @Output() readonly indeterminateChange = new EventEmitter<boolean>();

  private _checked = false;
  private _disabled = false;
  private _formDisabled = false;
  private _indeterminate = false;

  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  writeValue(value: boolean): void {
    this._checked = coerceBooleanProperty(value);
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._formDisabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  onCheckboxChange(event: MatCheckboxChange): void {
    this._checked = event.checked;
    this.onChange(this._checked);
    this.onTouched();
    this.change.emit(event);
    this.changeDetectorRef.markForCheck();
  }

  onIndeterminateChange(value: boolean): void {
    this._indeterminate = value;
    this.indeterminateChange.emit(value);
    this.changeDetectorRef.markForCheck();
  }
}
